import { navigateTo, useNuxtApp, useRoute } from '#imports'
import { storeToRefs } from 'pinia'
import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import { useIdentity } from '~/composables/useIdentity'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useSearchApplication } from '~/composables/useSearchApplication'
import { useStoreMenuItemPriceTypes } from '~/composables/useStoreMenuItemPriceTypes'
import { isStoreClosureProblem } from '~/composables/isStoreClosureProblem'
import { resolveAvailabilityProblemMessage } from '~/composables/resolveAvailabilityProblemMessage'
import { isOrderType, OrderType } from '~/enums'
import { OrderTypePresentation } from '~/presentation/OrderTypePresentation'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { useCartActions } from '~/composables/useCartActions'
import { useCartStore } from '~/stores/useCartStore'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { formatStoreAddress } from '~/utils/formatStoreAddress'
import { Normalization } from '~/utils/normalization'
import { refreshStoredIdentity } from '~/utils/refreshStoredIdentity'

interface StoreCartDrawerInputs {
  isOpen: Readonly<Ref<boolean | undefined>>
  store: Readonly<Ref<Record<string, unknown> | null | undefined>>
  isStoreUnavailable: Readonly<Ref<boolean | undefined>>
  storeUnavailableMessage: Readonly<Ref<string | null | undefined>>
  checkAvailability: Readonly<Ref<(() => Promise<boolean>) | null | undefined>>
  onClose: () => void
  onGuestCountRequired: () => void
  onAvailabilityFailed: (
    failure:
      | { reason: 'closure' | 'store-unavailable' | 'verification'; message: () => string }
      | {
          reason: 'availability' | 'verification'
          message: () => string
          data: Record<string, unknown>
        }
  ) => void
}

function getStringValue(record: Record<string, unknown> | undefined, key: string) {
  const value = record?.[key]
  return typeof value === 'string' ? value : undefined
}

function getFirstValidationMessage(errors: unknown) {
  if (!Normalization.isRecord(errors)) return undefined
  for (const value of Object.values(errors)) {
    if (typeof value === 'string') return value
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  }
  return undefined
}

function getEventLocation(
  orderType: unknown,
  searchQuery: unknown,
  store: Record<string, unknown> | undefined
): string {
  if (orderType === OrderType.DELIVERY) {
    if (typeof searchQuery === 'string') return searchQuery
    if (Normalization.isRecord(searchQuery))
      return (
        getStringValue(searchQuery, 'q') ??
        getStringValue(searchQuery, 'address') ??
        'Enter location'
      )
    return 'Enter location'
  }
  const address = formatStoreAddress({
    address: getStringValue(store, 'address') ?? null,
    city: getStringValue(store, 'city') ?? null,
    state: getStringValue(store, 'state') ?? null,
    zip: getStringValue(store, 'zip') ?? null
  })
  return address === '' ? 'Restaurant location' : address
}

/** Owns the store cart's availability admission, identity refresh and checkout transition. */
export function useStoreCartDrawer(inputs: StoreCartDrawerInputs) {
  const nuxtApp = useNuxtApp()
  const identity = useIdentity()
  let active = true
  onScopeDispose(() => {
    active = false
  })
  const cartStore = useCartStore()
  const cartActions = useCartActions()
  const { searchStore, fetchAvailability } = useSearchApplication()
  const route = useRoute()
  const t = usePresentationTranslator()
  const logger = useLogger('StoreCartDrawer')
  const { loadPriceTypes, getQuantityLabel } = useStoreMenuItemPriceTypes()
  const {
    storeItems,
    deliveryDate,
    storeSubtotal,
    hasDeliveryFee,
    deliveryFee,
    hasApplicationFee,
    applicationFeeValue,
    hasTax,
    tax,
    tipAmount,
    total,
    tipSelection,
    minimumOrder,
    minimumShortfall
  } = storeToRefs(cartStore)
  const { orderType } = storeToRefs(searchStore)
  const checkoutLoading = ref(false)
  const removingItemId = ref<{ provided: false } | { provided: true; value: number }>({
    provided: false
  })
  const temporarilyClosedStoreMessage = computed(() =>
    t('commerce.storeCartDrawer.temporarilyClosedStoreMessage')
  )
  const unavailableStoreMessage = computed(() => {
    const message = inputs.storeUnavailableMessage.value
    return typeof message === 'string' && message !== ''
      ? message
      : temporarilyClosedStoreMessage.value
  })
  const isCheckoutDisabled = computed(
    () =>
      checkoutLoading.value ||
      inputs.isStoreUnavailable.value === true ||
      minimumShortfall.value > 0
  )
  const formatCurrency = useCurrencyFormat()
  const perPersonCost = computed<{ provided: false } | { provided: true; value: string }>(() => {
    const guestCount = searchStore.guestCount
    return guestCount.provided
      ? { provided: true, value: formatCurrency(Number(total.value) / guestCount.value) }
      : { provided: false }
  })
  const orderTypeDisplay = computed(() =>
    isOrderType(orderType.value) ? OrderTypePresentation[orderType.value].label : 'Location'
  )
  const eventStoreName = computed(() => {
    const name = inputs.store.value?.['name']
    return orderType.value !== OrderType.DELIVERY && typeof name === 'string' ? name : ''
  })
  const eventLocation = computed(() =>
    getEventLocation(orderType.value, searchStore.searchQuery, inputs.store.value ?? undefined)
  )
  watch(
    [() => inputs.isOpen.value, () => cartStore.currentStoreSlug],
    ([isOpen, slug]) => {
      if (isOpen === true && typeof slug === 'string' && slug.length > 0) void loadPriceTypes(slug)
    },
    { immediate: true }
  )

  const closeCart = () => {
    cartStore.closeStoreCart()
    inputs.onClose()
  }
  const resolveCurrentStoreSlug = (): { provided: false } | { provided: true; value: string } => {
    const raw = Array.isArray(route.params['slug']) ? route.params['slug'][0] : route.params['slug']
    const slug = Normalization.trimmedStringValue(raw) ?? cartStore.currentStoreSlug
    return typeof slug === 'string' && slug.length > 0
      ? { provided: true, value: slug }
      : { provided: false }
  }
  const isCurrentStoreSlug = (slug: string) => {
    const current = resolveCurrentStoreSlug()
    return active && current.provided && current.value === slug
  }
  const proceedToCheckout = async (slug: string) => {
    const refreshIdentity = async () => {
      if (!isCurrentStoreSlug(slug)) return
      await identity.refreshIdentity()
    }
    try {
      await refreshStoredIdentity(nuxtApp, refreshIdentity, 'fresh')
    } catch (refreshError: unknown) {
      logger.warn('checkout', { refreshError })
    }
    if (!isCurrentStoreSlug(slug)) return
    closeCart()
    if (!identity.isAuthenticated.value) {
      if (import.meta.client) sessionStorage.setItem('checkoutStoreSlug', slug)
      void Promise.resolve(navigateTo('/login')).catch((navigationError: unknown) => {
        logger.error('checkout', { navigationError })
      })
    } else {
      void Promise.resolve(navigateTo('/checkout/' + slug)).catch((navigationError: unknown) => {
        logger.error('checkout', { navigationError })
      })
    }
  }
  const verifyCheckoutAvailability = async (slug: string, timezone: string): Promise<boolean> => {
    const hostCheck = inputs.checkAvailability.value
    if (typeof hostCheck === 'function') {
      const available = await hostCheck()
      return available && isCurrentStoreSlug(slug)
    }
    const response = await fetchAvailability(slug, { timezone })
    if (!isCurrentStoreSlug(slug)) return false
    const data = response.data
    if (!data.available) {
      inputs.onAvailabilityFailed({
        reason: 'availability',
        data,
        message: () =>
          getFirstValidationMessage('errors' in data ? data.errors : undefined) ??
          getStringValue(data, 'message') ??
          t('commerce.availabilityErrorModal.notAvailableForRequest')
      })
      return false
    }
    return true
  }
  const reportCheckoutVerificationFailure = (error: unknown, timezone: string): void => {
    const problem = AppProblemModel.normalize(error)
    if (isStoreClosureProblem(problem)) {
      inputs.onAvailabilityFailed({
        reason: 'closure',
        message: () =>
          resolveAvailabilityProblemMessage(
            problem,
            timezone,
            t,
            t('commerce.availabilityErrorModal.verifyFailed')
          )
      })
      return
    }
    const data =
      Normalization.isRecord(error) && Normalization.isRecord(error['data'])
        ? error['data']
        : undefined
    const message = () => {
      const verifyFailed = t('commerce.availabilityErrorModal.verifyFailed')
      return resolveAvailabilityProblemMessage(
        problem,
        timezone,
        t,
        verifyFailed,
        (_problem, _t, fallback) =>
          getFirstValidationMessage(data?.['errors']) ??
          getStringValue(data, 'message') ??
          (Normalization.isRecord(error) ? getStringValue(error, 'message') : undefined) ??
          fallback ??
          verifyFailed
      )
    }
    inputs.onAvailabilityFailed(
      data === undefined
        ? { reason: 'verification', message }
        : { reason: 'verification', message, data }
    )
  }
  const checkout = async () => {
    if (!active || minimumShortfall.value > 0) return
    if (inputs.isStoreUnavailable.value === true) {
      inputs.onAvailabilityFailed({
        reason: 'store-unavailable',
        message: () => unavailableStoreMessage.value
      })
      return
    }
    const current = resolveCurrentStoreSlug()
    if (!current.provided) {
      logger.error('checkout', { reason: 'no store slug available' })
      return
    }
    const slug = current.value
    if (searchStore.needsGuestCount) {
      inputs.onGuestCountRequired()
      return
    }
    checkoutLoading.value = true
    const rawTimezone = inputs.store.value?.['timezone']
    const timezone = typeof rawTimezone === 'string' ? rawTimezone : 'UTC'
    try {
      if (!(await verifyCheckoutAvailability(slug, timezone)) || !isCurrentStoreSlug(slug)) return
      await proceedToCheckout(slug)
    } catch (error: unknown) {
      if (!isCurrentStoreSlug(slug)) return
      reportCheckoutVerificationFailure(error, timezone)
    } finally {
      checkoutLoading.value = false
    }
  }
  const removeItem = async (itemId: number) => {
    const raw = Array.isArray(route.params['slug']) ? route.params['slug'][0] : route.params['slug']
    const slug = Normalization.trimmedStringValue(raw)
    if (
      typeof slug !== 'string' ||
      slug.length === 0 ||
      (removingItemId.value.provided && removingItemId.value.value === itemId)
    )
      return
    removingItemId.value = { provided: true, value: itemId }
    try {
      await cartActions.removeItem(itemId, slug)
    } finally {
      removingItemId.value = { provided: false }
    }
  }
  return {
    storeItems,
    deliveryDate,
    storeSubtotal,
    hasDeliveryFee,
    deliveryFee,
    hasApplicationFee,
    applicationFeeValue,
    hasTax,
    tax,
    tipAmount,
    total,
    tipSelection,
    minimumOrder,
    minimumShortfall,
    orderType,
    checkoutLoading,
    removingItemId,
    perPersonCost,
    orderTypeDisplay,
    eventStoreName,
    eventLocation,
    unavailableStoreMessage,
    isCheckoutDisabled,
    getItemQuantityLabel: getQuantityLabel,
    closeCart,
    checkout,
    removeItem
  }
}
